import { gql, useSubscription } from '@apollo/client';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  Stack,
  useToast,
} from '@chakra-ui/react';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import * as React from 'react';

import { StripeElements } from '@/components/StripeElements';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { UnexpectedError } from '@/imports/ui/chakra/components/UnexpectedError';

import {
  AddPaymentMethodModal_OnPaymentMethodAdded,
  AddPaymentMethodModal_OnPaymentMethodAddedVariables,
} from './graphql-types/AddPaymentMethodModal_OnPaymentMethodAdded';

import { useSetupIntentClientSecret } from './hooks/useSetupIntentClientSecret';

const ON_PAYMENT_METHOD_ADDED = gql`
  subscription AddPaymentMethodModal_OnPaymentMethodAdded(
    $data: OnPaymentMethodAddedInput!
  ) {
    onPaymentMethodAdded(data: $data) {
      user {
        _id
        paymentMethods {
          _id
          isDefault
        }
      }
      organization {
        _id
        paymentMethods {
          _id
          isDefault
        }
      }
    }
  }
`;

const _AddPaymentMethodModal: React.FC<{
  onClose: () => void;
  userId: string | undefined;
  organizationId: string | undefined;
}> = ({ onClose, userId, organizationId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();

  const [isLoading, setIsLoading] = React.useState(false);
  const [setupError, setSetupError] = React.useState<Error | null>(null);

  useSubscription<
    AddPaymentMethodModal_OnPaymentMethodAdded,
    AddPaymentMethodModal_OnPaymentMethodAddedVariables
  >(ON_PAYMENT_METHOD_ADDED, {
    variables: { data: { userId, organizationId } },
    onSubscriptionData() {
      setIsLoading(false);

      toast({
        status: 'success',
        description: 'Payment method saved!',
      });

      onClose();
    },
  });

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      setSetupError(null);

      if (!stripe) {
        throw new Error('Stripe Not Found');
      }

      if (!elements) {
        throw new Error('Elements Not Found');
      }

      const cardSetup = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (cardSetup.error?.message) {
        setIsLoading(false);
        setSetupError(new Error(cardSetup.error.message));
      }
    } catch (error) {
      setSetupError(error);
      setIsLoading(false);
    }
  };

  return (
    <>
      <ModalHeader>Add a Payment Method</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Stack spacing="4">
          {setupError && (
            <Alert status="error" variant="subtle">
              <AlertIcon />
              <Stack spacing="0">
                <AlertTitle>Adding Payment Method Failed</AlertTitle>
                <AlertDescription>{setupError.message}</AlertDescription>
              </Stack>
            </Alert>
          )}

          <PaymentElement />
        </Stack>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="outline"
          colorScheme="gray"
          mr="1"
          onClick={onClose}
          isDisabled={isLoading}
        >
          Cancel
        </Button>
        <Button onClick={onSubmit} isLoading={isLoading}>
          Save
        </Button>
      </ModalFooter>
    </>
  );
};

export const AddPaymentMethodModal: React.FC<
  {
    userId: string | undefined;
    organizationId: string | undefined;
  } & Omit<ModalProps, 'children'>
> = ({ userId, organizationId, ...modalProps }) => {
  const { clientSecret, isLoading } = useSetupIntentClientSecret({
    userId,
    organizationId,
    skip: !modalProps.isOpen,
  });

  return (
    <Modal isCentered {...modalProps} size="3xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="95vh">
        {isLoading ? (
          <Loading />
        ) : !clientSecret ? (
          <UnexpectedError />
        ) : (
          <StripeElements options={{ clientSecret }}>
            <_AddPaymentMethodModal
              onClose={modalProps.onClose}
              userId={userId}
              organizationId={organizationId}
            />
          </StripeElements>
        )}
      </ModalContent>
    </Modal>
  );
};
